import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { ReactNode, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { formatCurrency } from '../../../../components';
import { getDiscountPrice, getUnitPrice } from '../../../../hook';
import { Feature, PlanInterval } from '../../../../models';
import { useLaunchDarkly } from '../../../../launchdarkly';
import { fromPromotion, useAppDispatch } from '../../../../store';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS } from '../../../../utils/Constants';

// Default number of discounted seats when the promotion payload omits maxDiscountLicense.
const DEFAULT_DISCOUNT_SEATS = 3;

const useStyles = makeStyles(theme => ({
  '@keyframes offerSheen': {
    '0%': { transform: 'translateX(-160%)' },
    '45%, 100%': { transform: 'translateX(340%)' },
  },
  band: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    backgroundColor: '#fdf6e3',
    borderBottom: '1px solid #ece0b3',
    padding: theme.spacing(1.625, 5),
    // Bleed across the page body's horizontal padding, like the step nav above it.
    margin: theme.spacing(0, -5),
  },
  // Light sweep across the band every ~4s (from the design mock). Sits under the
  // content (zIndex 0 vs 1) and is disabled for users preferring reduced motion.
  sheen: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    zIndex: 0,
    pointerEvents: 'none',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: '55%',
      background: 'linear-gradient(100deg, transparent 0%, rgba(255,255,255,.9) 48%, rgba(254,215,48,.35) 58%, transparent 100%)',
      transform: 'translateX(-160%)',
      animation: '$offerSheen 4.2s ease-in-out infinite',
    },
    '@media (prefers-reduced-motion: reduce)': {
      '&::before': {
        animation: 'none',
        display: 'none',
      },
    },
  },
  badge: {
    position: 'relative',
    zIndex: 1,
    flex: 'none',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    color: '#5a4a00',
    backgroundColor: '#fed730',
    borderRadius: 999,
    padding: theme.spacing(0.625, 1.5),
  },
  message: {
    position: 'relative',
    zIndex: 1,
    fontSize: 13.5,
    fontWeight: 500,
    lineHeight: 1.4,
    color: '#16141d',
  },
  infoIcon: {
    position: 'relative',
    zIndex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'help',
  },
  tooltip: {
    maxWidth: 300,
    padding: theme.spacing(1.75, 2),
    backgroundColor: '#16141d',
    color: '#d9d8e6',
    border: '1px solid #2c2940',
    fontFamily: 'Inter, sans-serif',
    fontSize: 12.3,
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: 12,
    boxShadow: '0 16px 40px rgba(16,20,45,.32)',
  },
  tooltipTitle: {
    color: '#ffffff',
    fontFamily: 'Inter, sans-serif',
    fontSize: 12.5,
    fontWeight: 700,
    marginBottom: theme.spacing(0.75),
  },
  tooltipList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    '& li': {
      position: 'relative',
      color: '#d9d8e6',
      fontSize: 12.5,
      lineHeight: 1.5,
      paddingLeft: theme.spacing(1.625),
      marginBottom: theme.spacing(0.75),
    },
    '& li::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 6,
      width: 5,
      height: 5,
      borderRadius: '50%',
      backgroundColor: '#19d89f',
    },
    '& li:last-child': {
      marginBottom: 0,
    },
  },
}));

/**
 * "Limited offer" band under the checkout step nav (July 2026 payment & billing design):
 * True Automation's first-3-seats annual promo expressed as a per-month price.
 *
 * Visibility follows the account's promotion eligibility: it only renders when
 * `GET /v2/promotion/active` returns an active campaign for this account (the BE already
 * enforces campaign dates, blacklist, new-account and remaining-quota rules). Prices are
 * derived from the LD plan-price flags — the `/active` payload carries no product prices.
 */
export const LimitedOfferBanner = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { flags } = useLaunchDarkly();
  const activePromotion = useSelector(fromPromotion.selectActivePromotion);

  // Fetch the active promotion if it has not been loaded yet (e.g. user lands directly
  // on the plan page without visiting the subscription page first) — same pattern as
  // the legacy PromoBanner.
  useEffect(() => {
    if (!activePromotion) {
      dispatch(fromPromotion.doFetchActivePromotion());
    }
  }, [activePromotion, dispatch]);

  const listedAnnual = getUnitPrice(
    flags?.subscriptionPlanPrice,
    Feature.TRUE_AUTOMATION,
    PlanInterval.YEAR,
    'product',
  );
  const promoAnnual = getDiscountPrice(
    flags?.subscriptionPlanPrice,
    Feature.TRUE_AUTOMATION,
    PlanInterval.YEAR,
  );

  // Visibility is decided by promotion eligibility alone. The LD discount price only
  // affects HOW the message renders (with or without the price pair), never WHETHER.
  if (!activePromotion) {
    return null;
  }

  const hasPromoPrice = promoAnnual > 0 && promoAnnual < listedAnnual;

  const seatCount = ('maxDiscountLicense' in activePromotion
    ? activePromotion.maxDiscountLicense
    : undefined) ?? DEFAULT_DISCOUNT_SEATS;
  // The single-product `/active` payload does not stamp the campaign endDate; the
  // uiConfig timeWindow (when configured) is the closest source, then endDate itself.
  const endTimestamp = ('timeWindow' in activePromotion
    ? activePromotion.timeWindow?.endDate
    : undefined) ?? activePromotion.endDate;
  const endDateText = endTimestamp
    ? intl.formatDate(endTimestamp, { month: 'long', day: 'numeric', year: 'numeric' })
    : intl.formatMessage({ id: 'page.plan.v2.offer.banner.end.fallback' });

  return (
    <Box id="plan-v2-limited-offer-banner" className={classes.band}>
      <Box component="span" aria-hidden className={classes.sheen} />
      <Typography component="span" className={classes.badge}>
        <FormattedMessage id="page.plan.v2.offer.banner.badge" />
      </Typography>
      <Typography component="span" className={classes.message}>
        <FormattedMessage
          id={hasPromoPrice
            ? 'page.plan.v2.offer.banner.message'
            : 'page.plan.v2.offer.banner.message.no.price'}
          values={{
            listedPrice: formatCurrency(
              intl,
              Math.round(listedAnnual / 12),
              DEFAULT_CURRENCY_FORMAT_OPTIONS,
            ),
            promoPrice: formatCurrency(
              intl,
              Math.round(promoAnnual / 12),
              DEFAULT_CURRENCY_FORMAT_OPTIONS,
            ),
            seatCount,
            endDate: endDateText,
            b: (chunks: ReactNode) => (
              <Box component="span" sx={{ fontWeight: 700 }}>{chunks}</Box>
            ),
            strike: (chunks: ReactNode) => (
              <Box component="span" sx={{ textDecoration: 'line-through', color: '#8a919e' }}>
                {chunks}
              </Box>
            ),
            ends: (chunks: ReactNode) => (
              <Box component="span" sx={{ fontWeight: 700, color: '#8a6d00' }}>{chunks}</Box>
            ),
          }}
        />
      </Typography>
      <Tooltip
        disableInteractive
        placement="bottom-start"
        classes={{ tooltip: classes.tooltip }}
        title={(
          <Box>
            <Typography className={classes.tooltipTitle}>
              <FormattedMessage id="page.plan.v2.offer.banner.tooltip.title" />
            </Typography>
            <ul className={classes.tooltipList}>
              <li><FormattedMessage id="page.plan.v2.offer.banner.tooltip.discounted" /></li>
              <li><FormattedMessage id="page.plan.v2.offer.banner.tooltip.exclusion" /></li>
            </ul>
          </Box>
        )}
      >
        <Box
          component="span"
          role="img"
          aria-label={intl.formatMessage({ id: 'page.plan.v2.offer.banner.tooltip.title' })}
          className={classes.infoIcon}
        >
          <svg
            width={15}
            height={15}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#8a6d00"
            strokeWidth={1.7}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        </Box>
      </Tooltip>
    </Box>
  );
};
